import React from "react";
import { useAuth } from "../../Context/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTheme } from "../../Context/ThemeContext";
import { motion } from "framer-motion";
import { FaSun, FaMoon, FaUser, FaSignOutAlt } from "react-icons/fa";

const Header = () => {
  const { darkMode, toggleTheme } = useTheme();
  const [auth, setAuth] = useAuth();
  const navigate = useNavigate();

  // Responsive styles
  const styles = {
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0.8rem clamp(1rem, 3vw, 2rem)",
      background: darkMode
        ? "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
        : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      position: "sticky",
      top: 0,
      zIndex: 1000,
      backdropFilter: "blur(8px)",
      borderBottom: darkMode
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(255, 255, 255, 0.3)",
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      gap: "0.8rem",
    },
    logo: {
      fontSize: "clamp(1.5rem, 3vw, 1.8rem)",
      filter: "drop-shadow(0 0 5px rgba(255, 255, 255, 0.5))",
    },
    titleGroup: {
      display: "flex",
      flexDirection: "column",
    },
    title: {
      fontSize: "clamp(1.1rem, 2vw, 1.4rem)",
      fontWeight: "700",
      color: "white",
      margin: 0,
      lineHeight: "1.2",
      whiteSpace: "nowrap",
    },
    subtitle: {
      fontSize: "clamp(0.6rem, 1.5vw, 0.75rem)",
      color: "rgba(255, 255, 255, 0.8)",
      margin: 0,
      fontWeight: "400",
      whiteSpace: "nowrap",
    },
    buttonGroup: {
      display: "flex",
      alignItems: "center",
      gap: "0.6rem",
    },
    buttonBase: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.4rem",
      padding: "0.5rem clamp(0.8rem, 2vw, 1.2rem)",
      borderRadius: "50px",
      cursor: "pointer",
      fontWeight: "600",
      fontSize: "clamp(0.7rem, 2vw, 0.9rem)",
      transition: "all 0.3s ease",
      border: "none",
      outline: "none",
      minWidth: "fit-content",
    },
    themeButton: {
      background: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
      color: "white",
    },
    userBadge: {
      background: "rgba(255, 255, 255, 0.1)",
      color: "white",
      padding: "0.4rem 0.8rem",
      borderRadius: "50px",
      fontSize: "clamp(0.7rem, 2vw, 0.85rem)",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      "@media (max-width: 500px)": {
        display: "none", // Hide on very small screens
      },
    },
    logoutButton: {
      background: "rgba(255, 59, 48, 0.9)",
      color: "white",
    },
    icon: {
      fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
    },
  };

  const handleLogout = () => {
    setAuth({
      ...auth,
      user: null,
      token: "",
    });
    localStorage.removeItem("auth");
    toast.success("Logged out successfully!", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: darkMode ? "dark" : "light",
    });
    setTimeout(() => {
      navigate("/login");
    }, 1000);
  };

  return (
    <>
      <motion.div
        style={styles.header}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", damping: 20 }}
      >
        <div style={styles.titleContainer}>
          <motion.span
            style={styles.logo}
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 5 }}
          >
            💎
          </motion.span>
          <div style={styles.titleGroup}>
            <h1 style={styles.title}>Bloopay</h1>
            <p style={styles.subtitle}>Payment made simple</p>
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <motion.button
            style={{ ...styles.buttonBase, ...styles.themeButton }}
            onClick={toggleTheme}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {darkMode ? (
              <>
                <FaSun style={styles.icon} />
              </>
            ) : (
              <>
                <FaMoon style={styles.icon} />
              </>
            )}
          </motion.button>

          {auth?.user && (
            <>
              <motion.button
                style={{ ...styles.buttonBase, ...styles.logoutButton }}
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaSignOutAlt style={styles.icon} />
                <span>Logout</span>
              </motion.button>
            </>
          )}
        </div>
      </motion.div>
      <ToastContainer theme={darkMode ? "dark" : "light"} />
    </>
  );
};

export default Header;
